import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { formatCompactINR } from "@/lib/format";

// Hoisted static config to avoid re-allocating on each render
const CHART_MARGIN = { top: 10, right: 10, bottom: 0, left: 0 };
const GRID_STROKE = "rgba(255,255,255,0.05)";
const AXIS_STROKE = "rgba(255,255,255,0.08)";
const AXIS_LINE = { stroke: AXIS_STROKE };
const TICK_STYLE = { fill: "#94A3B8", fontSize: 12 };
const REF_LABEL_BASE = { fill: "#FDBA74", fontSize: 11, position: "insideTopRight" };
const TOOLTIP_LABELS = {
  corpusNormal: "Normal",
  corpusStepup: "Step-up",
};
const yTickFormatter = (v) => formatCompactINR(v);

function TooltipContent({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-white/10 bg-[#0A0F1C]/95 backdrop-blur px-3 py-2 text-xs shadow-xl">
      <div className="text-slate-400 mb-1">Age {label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 num-font">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: p.color }}
          />
          <span className="text-slate-300">
            {TOOLTIP_LABELS[p.dataKey] || p.dataKey}:
          </span>
          <span className="text-white font-medium">
            {formatCompactINR(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function CorpusChart({ plan }) {
  const data = plan.corpusGrowth;
  const target = plan.targetCorpus;

  return (
    <section
      className="mt-10 rounded-2xl border border-white/10 bg-[#111827]/70 p-6 sm:p-8"
      data-testid="corpus-chart-section"
    >
      <div className="flex items-start justify-between flex-wrap gap-2 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#F97316]">
            Corpus Growth
          </div>
          <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-semibold tracking-tight">
            Your wealth curve to freedom
          </h3>
          <p className="mt-2 text-sm text-slate-400 max-w-xl">
            Watch your investments compound year after year until they cross the target line.
          </p>
        </div>
        <div className="flex items-center gap-5 text-xs text-slate-400">
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full bg-[#F97316]" />
            Step-up SIP
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full bg-sky-400" />
            Normal SIP
          </div>
        </div>
      </div>

      <div className="h-[320px] sm:h-[420px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={CHART_MARGIN}>
            <defs>
              <linearGradient id="grad-stepup" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F97316" stopOpacity={0.7} />
                <stop offset="100%" stopColor="#F97316" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="grad-normal" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#38BDF8" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#38BDF8" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={GRID_STROKE} strokeDasharray="3 3" />
            <XAxis
              dataKey="age"
              stroke="#64748B"
              tick={TICK_STYLE}
              tickLine={false}
              axisLine={AXIS_LINE}
            />
            <YAxis
              stroke="#64748B"
              tick={TICK_STYLE}
              tickFormatter={yTickFormatter}
              tickLine={false}
              axisLine={AXIS_LINE}
              width={80}
            />
            <Tooltip content={<TooltipContent />} />
            <ReferenceLine
              y={target}
              stroke="#FDBA74"
              strokeDasharray="4 4"
              label={{ ...REF_LABEL_BASE, value: `Target ${formatCompactINR(target)}` }}
            />
            <Area
              type="monotone"
              dataKey="corpusNormal"
              stroke="#38BDF8"
              strokeWidth={2}
              fill="url(#grad-normal)"
              isAnimationActive
            />
            <Area
              type="monotone"
              dataKey="corpusStepup"
              stroke="#F97316"
              strokeWidth={2.5}
              fill="url(#grad-stepup)"
              isAnimationActive
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
